import React, { useState, useEffect } from 'react';
import { Box, Skeleton } from '@mui/material';

/**
 * AG Grid için skeleton (iskelet) yükleme bileşeni
 * @param {Object} props - Bileşen özellikleri
 * @param {number} props.rowCount - Gösterilecek skeleton satır sayısı (varsayılan: 6)
 * @param {number} props.columnCount - Gösterilecek sütun sayısı (varsayılan: 5)
 * @param {string|number} props.height - Skeleton grid yüksekliği (varsayılan: 400)
 * @param {Array} props.headerText - Sütun başlıklarının metinleri
 * @param {boolean} props.showHeaders - Başlıkların anında gösterilmesi (varsayılan: true)
 * @param {number} props.headerLoadDelay - Başlıkların gecikmeli yüklenmesi (ms cinsinden) (varsayılan: 300)
 */
export const AgGridSkeleton = ({
  rowCount = 6,
  columnCount = 5,
  height = 400,
  headerText = ["İşlemler", "Sipariş Tarihi", "Bayi", "Müşteri", "Durum"], // Varsayılan başlık metinleri
  showHeaders = true, // Başlıkların hemen gösterilmesini kontrol eder
  headerLoadDelay = 300, // Başlıkların yüklenme gecikmesi (ms)
}) => {
  const [headersLoaded, setHeadersLoaded] = useState(showHeaders);

  // Başlıkların yüklenmesini simüle et (eğer showHeaders false ise)
  useEffect(() => {
    if (!showHeaders) {
      const timer = setTimeout(() => {
        setHeadersLoaded(true);
      }, headerLoadDelay);
      return () => clearTimeout(timer);
    }
  }, [showHeaders, headerLoadDelay]);

  // Başlıkları oluştur
  const renderHeaders = () => (
    <Box
      sx={{
        display: 'flex',
        borderBottom: '1px solid #dde2eb',
        height: '48px',
        backgroundColor: '#f8f8f8'
      }}
    >
      {[...Array(columnCount)].map((_, index) => (
        <Box
          key={`header-${index}`}
          sx={{
            flex: 1,
            borderRight: index !== columnCount - 1 ? '1px solid #dde2eb' : 'none',
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center',
            fontWeight: 'bold',
            fontSize: '0.875rem',
            color: '#333'
          }}
        >
          {headersLoaded ?
            (headerText && headerText[index] ? headerText[index] : `Sütun ${index + 1}`) :
            <Skeleton variant="text" width="80%" height={24} />
          }
        </Box>
      ))}
    </Box>
  );

  // Veri satırlarını oluştur
  const renderRows = () => (
    <Box sx={{ height: 'calc(100% - 48px)', overflow: 'hidden' }}>
      {[...Array(rowCount)].map((_, rowIndex) => (
        <Box
          key={`row-${rowIndex}`}
          sx={{
            display: 'flex',
            borderBottom: '1px solid #f0f0f0',
            height: '48px'
          }}
        >
          {[...Array(columnCount)].map((_, colIndex) => (
            <Box
              key={`cell-${rowIndex}-${colIndex}`}
              sx={{
                flex: 1,
                borderRight: colIndex !== columnCount - 1 ? '1px solid #f0f0f0' : 'none',
                padding: '0 8px',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Skeleton
                variant="text"
                width={getSkeletonWidth(colIndex)}
                height={24}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      ))}
    </Box>
  );

  // Farklı sütunlar için farklı genişlikler
  const getSkeletonWidth = (colIndex) => {
    // İlk sütun genellikle daha kısa (ör. işlem butonları)
    if (colIndex === 0) return '60%';
    // Son sütun genellikle durum gibi kısa içerikler
    if (colIndex === columnCount - 1) return '50%';
    // Tarih sütunları orta uzunlukta
    if (colIndex === 1) return '70%';
    // Diğer sütunlar daha uzun olabilir
    return Math.floor(70 + Math.random() * 20) + '%';
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: typeof height === 'number' ? `${height}px` : height,
        border: '1px solid #dde2eb',
        borderRadius: '4px',
        overflow: 'hidden',
        backgroundColor: '#fff',
        fontFamily: 'inherit'
      }}
    >
      {renderHeaders()}
      {renderRows()}
    </Box>
  );
};

export default AgGridSkeleton;